import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const PRODUCT =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_82d11e62-96d8-4a58-bd86-23180e6879b5/artifacts/y4cty4ct_WhatsApp%20Image%202026-08-13%20at%207.57.24%20AM.jpeg";

const lines = ["Seal.", "Protect.", "Heal."];

const MaskLine = ({ children, delay }) => (
  <span className="reveal-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      ref={ref}
      className="relative min-h-[80vh] pt-16 md:pt-24 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <motion.div style={{ y: textY }} className="md:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-10 bg-secondary" />
            <span className="font-monotech text-xs tracking-[0.25em] uppercase text-text-muted">
              Medical-Grade Super Adhesive
            </span>
          </motion.div>

          <h1 className="font-heading font-extrabold text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] tracking-tighter leading-[0.85] text-primary">
            {lines.map((l, i) => (
              <MaskLine key={l} delay={0.3 + i * 0.15}>
                {l}
              </MaskLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-text-muted"
          >
            An advanced bioactive wound-closure solution engineered to seal, protect and
            accelerate natural healing. <span className="font-serifaccent italic text-primary text-xl">Your wound, our care.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#products"
              data-testid="hero-explore-btn"
              className="rounded-full bg-primary text-white px-8 py-3.5 text-sm font-semibold hover:bg-accent transition-colors duration-300"
            >
              Explore Products
            </a>
            <a
              href="#how-it-works"
              data-testid="hero-how-btn"
              className="rounded-full border border-primary/20 text-primary px-8 py-3.5 text-sm font-semibold hover:bg-primary hover:text-white transition-colors duration-300"
            >
              How It Works
            </a>
          </motion.div>
        </motion.div>

        <div className="md:col-span-5 relative">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            className="relative z-10"
          >
            <div className="clip-capsule overflow-hidden border border-border shadow-2xl bg-gradient-to-br from-secondary/25 via-secondary/5 to-primary/10 relative">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(235,184,80,0.4),transparent_60%)]" />
              <img
                src={PRODUCT}
                alt="Healseeal product"
                className="relative z-10 w-full h-[440px] md:h-[560px] object-contain p-8"
                data-testid="hero-product-image"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-primary text-white rounded-2xl px-5 py-4 shadow-xl">
              <div className="font-heading font-bold text-2xl leading-none">12s</div>
              <div className="font-monotech text-[10px] tracking-widest uppercase text-secondary mt-1">
                To Seal
              </div>
            </div>
          </motion.div>
          <div className="absolute -top-10 -right-6 w-40 h-40 rounded-full bg-secondary/20 blur-3xl z-0" />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="hidden md:flex items-center gap-2 absolute bottom-8 left-1/2 -translate-x-1/2 text-text-muted"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="font-monotech text-[10px] tracking-[0.25em] uppercase">Scroll</span>
      </motion.div>
    </section>
  );
};
